/* eslint-disable camelcase */
import * as React from 'react';

import { CollectionsModule, getModuleLayoutConfig } from '@coursera-web/cds-labs';
import type { Filter, HeaderLevel } from '@coursera-web/cds-labs';

import type { ButtonProps } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import useCollection from 'bundles/campaigns/hooks/useCollection';
import type { CarouselCollection } from 'bundles/collections-carousel/types/CarouselCollection';
import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';
import type { PromoTemplateCollectionSet } from 'bundles/promo-template/__generated__/baseContentfulTypes';
import type { PromoTemplateCollectionsFragment } from 'bundles/promo-template/__generated__/contentfulTypes';
import { useNativeDataContext } from 'bundles/promo-template/components/NativeDataContext';
import type { NativeDataRegistryEntryProgram } from 'bundles/promo-template/components/NativeDataContext';
import NativeError from 'bundles/promo-template/contentful/modules/native/NativeError';
import buildLinkHref from 'bundles/promo-template/utils/buildLinkHref';
import CollectionsSchemaMarkup from 'bundles/seo/components/CollectionsSchemaMarkup';

export type Props = {
  content: PromoTemplateCollectionsFragment & {
    preloadImage?: boolean;
    lazyLoadImage?: boolean;
    removeTopPadding?: boolean;
    headlineComponent?: HeaderLevel;
  };
  collectionsData?: CarouselCollection[];
  disableCrawling?: boolean;
};

function FilterableCollections({ content, collectionsData, disableCrawling }: Props) {
  const { isDataSourceLoading, getNativeData } = useNativeDataContext();
  const isProgramLoading = isDataSourceLoading();
  const program = isProgramLoading ? undefined : getNativeData<NativeDataRegistryEntryProgram>('program');
  const track = useTracker();

  const [currentCollectionSetIndex, setCurrentCollectionSetIndex] = React.useState(0);

  const filterTrackingName = (filterName: string) =>
    `collections_filter_chip_${filterName
      .toLocaleLowerCase()
      .split(/\s&\s|\s+/)
      .join('_')
      .replace(/-/, '_')}`;

  const handleFilterChange = (index: number, filterName: string) => {
    track('click_button', {
      button: {
        name: 'collections_filter_chip',
        linkName: filterTrackingName(filterName),
      },
      pageSection: {
        sectionName: 'promo_template_collections',
      },
    });
    setCurrentCollectionSetIndex(index);
  };

  const collectionSets = content.collectionSetsCollection?.items ?? [];
  const currentCollectionSet = collectionSets[currentCollectionSetIndex] as PromoTemplateCollectionSet | undefined;

  // TODO: After full launch of CMS Proxy BFF, remove shouldUseCmsProxyBff / references
  const shouldUseCmsProxyBff = GrowthDiscoveryEpicClient.get('cmsProxyBffEnabled');

  // Used for initially rendered collection data on consumer pages (no filtered collections)
  const bffCollectionData =
    shouldUseCmsProxyBff &&
    collectionsData &&
    collectionsData.find(
      (bffCollection) =>
        bffCollection.collectionId === currentCollectionSet?.collectionModuleId &&
        (currentCollectionSet.presetCollectionId
          ? bffCollection.collectionId === currentCollectionSet.presetCollectionId
          : true)
    );
  const collectionsToDisplay = bffCollectionData ? [bffCollectionData] : [];

  // Used for collection data on enterprise pages, and filtered collections on consumer pages
  const { loading: isCollectionLoading, collection } = useCollection({
    queryCollectionId: currentCollectionSet?.collectionModuleId ?? '',
    enterpriseProgramSlug: program?.slug,
    additionalInput: {
      preset_collection_id: currentCollectionSet?.presetCollectionId ?? '',
      enterprise_program_id: program?.id,
    },
    skip: isProgramLoading || (bffCollectionData && currentCollectionSetIndex === 0),
  });

  if (collection) {
    collectionsToDisplay.push(collection);
  }

  if (!collectionSets.length) {
    return <NativeError msg="Collection Sets missing" />;
  }

  const ctaHref = buildLinkHref(content?.ctaTarget, program?.slug);

  const trackClick = () => {
    track('click_button', {
      button: { name: 'promo_template_primary_cta', linkURL: ctaHref ?? '' },
      pageSection: {
        sectionName: 'promo_template_collections',
      },
    });
  };

  const configurationErrors: string[] = [];
  const filters: Filter[] = [];
  collectionSets.forEach((collectionSet, index) => {
    if (!collectionSet?.collectionModuleId) {
      configurationErrors.push(
        `[Collections] Collection Set ${index + 1} Missing required arguments: collectionModuleId`
      );
    }

    if (collectionSet?.filterName) {
      filters.push({
        id: collectionSet.sys.id,
        filterName: collectionSet.filterName,
        filterImage: collectionSet.filterImage,
      });
    }
  });

  if (configurationErrors.length > 0) {
    return (
      <>
        {configurationErrors.map((error) => (
          <NativeError key={error} msg={error} />
        ))}
      </>
    );
  }

  return (
    <>
      <CollectionsModule
        {...getModuleLayoutConfig(content)}
        {...{
          filters,
          currentCollectionSetIndex,
          collections: collectionsToDisplay,
          isCollectionsLoading: isCollectionLoading,
          onFilterChange: handleFilterChange,
          showAllEntries: content.showAllEntries,
          description: content.description,
          headline: content.headline,
          headlineComponent: content.headlineComponent,
          numOfEntriesPerCollection: content.numOfEntriesPerCollection ?? undefined,
          cta: {
            target: ctaHref,
            variant: content.ctaVariant as ButtonProps['variant'],
            text: content.ctaText,
            onClick: trackClick,
          },
        }}
      />
      {!disableCrawling && bffCollectionData && (
        <CollectionsSchemaMarkup
          headline={content.headline}
          description={content.description}
          collections={collectionsToDisplay}
        />
      )}
    </>
  );
}

export default FilterableCollections;
